'use client';

import { useMemo } from 'react';
import type { CalendarEvent, LensId } from '@/lib/types';
import { formatDayShort, formatDateShort } from '@/lib/date-format';
import { getEventSlug } from '@/lib/event-slugs';

interface PosterOverviewProps {
  events: CalendarEvent[];
  year: number;
  lensLabel: string;
  entityLabel: string;
  entityIcon: string;
  lens?: LensId;
  lang?: string;
}

const HEADER_COLORS = [
  'var(--poster-color-1, #2e7d32)',
  'var(--poster-color-2, #0d6e7a)',
  'var(--poster-color-3, #b36b00)',
  'var(--poster-color-4, #a0522d)',
  'var(--poster-color-5, #1565a0)',
] as const;

function isJointLeave(event: CalendarEvent): boolean {
  const title = `${event.title} ${event.titleLocal ?? ''}`.toLowerCase();
  const impact = (event.businessImpact ?? '').toLowerCase();
  return (
    title.includes('cuti bersama') ||
    title.includes('joint holiday') ||
    impact.includes('cuti bersama') ||
    impact.includes('joint leave')
  );
}

function dateTag(event: CalendarEvent, lang: string): string {
  const startLabel = `${formatDayShort(event.dateStart, lang)}, ${formatDateShort(event.dateStart, lang)}`;
  if (event.dateStart === event.dateEnd) return startLabel;
  const endLabel = `${formatDayShort(event.dateEnd, lang)}, ${formatDateShort(event.dateEnd, lang)}`;
  return `${startLabel} - ${endLabel}`;
}

function titleLine(event: CalendarEvent): string {
  if (event.titleLocal) return event.titleLocal;
  return event.title;
}

function subLine(event: CalendarEvent): string {
  if (event.titleLocal && event.title !== event.titleLocal) return event.title;
  return event.businessImpactLevel ? event.businessImpactLevel.toUpperCase() : event.type.replaceAll('_', ' ');
}

function getAcademicYearRange(events: CalendarEvent[]): string {
  if (events.length === 0) return '';
  const years = events.map(e => parseInt(e.dateStart.slice(0, 4), 10));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  return minYear === maxYear ? `${minYear}/${minYear + 1}` : `${minYear}/${maxYear}`;
}

function groupBySemester(events: CalendarEvent[]): { odd: CalendarEvent[]; even: CalendarEvent[] } {
  const odd: CalendarEvent[] = [];
  const even: CalendarEvent[] = [];
  for (const event of events) {
    const month = parseInt(event.dateStart.slice(5, 7), 10);
    if (month >= 7) {
      odd.push(event);
    } else {
      even.push(event);
    }
  }
  return { odd, even };
}

function findSemesterStartMs(events: CalendarEvent[]): number | null {
  const termStart = events.find(e => e.type === 'school_term_start');
  return termStart ? termStart.startMs : null;
}

function semesterWeek(event: CalendarEvent, semesterStartMs: number | null): number {
  if (!semesterStartMs) {
    // Fallback: reference Jul 1 (odd) or Jan 1 (even)
    const month = new Date(event.startMs).getMonth();
    const ref = new Date(event.year, month >= 6 ? 6 : 0, 1);
    return Math.floor((event.startMs - ref.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  }
  const diff = event.startMs - semesterStartMs;
  return Math.max(1, Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1);
}

function EventCard({ event, idx, showWeek, semesterStart, lang = 'en' }: { event: CalendarEvent; idx: number; showWeek?: boolean; semesterStart?: number | null; lang?: string }) {
  return (
    <article className="overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated shadow-sm">
      <div
        className="px-2 py-1 text-center text-label font-semibold uppercase tracking-wider text-white"
        style={{ backgroundColor: HEADER_COLORS[idx % HEADER_COLORS.length] }}
      >
        {dateTag(event, lang)}
      </div>
      <div className="px-3 pb-3 pt-2 text-center">
        {showWeek && (
          <p className="mb-0.5 text-caption font-bold uppercase tracking-wider text-brand">Wk {semesterWeek(event, semesterStart ?? null)}</p>
        )}
        <p className="text-xs font-semibold leading-tight text-text-primary">
          <a href={`/events/${getEventSlug(event.id)}`} className="hover:underline">{titleLine(event)}</a>
        </p>
        <p className="mt-1 text-label leading-snug text-text-tertiary">{subLine(event)}</p>
      </div>
    </article>
  );
}

export function PosterOverview({ events, year, lensLabel, entityLabel, entityIcon, lens, lang = 'en' }: PosterOverviewProps) {
  const isAcademic = lens === 'academic';

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.startMs - b.startMs),
    [events]
  );

  const jointLeaveEvents = useMemo(() => sortedEvents.filter(isJointLeave), [sortedEvents]);
  const nationalEvents = useMemo(
    () => sortedEvents.filter((event) => !isJointLeave(event)).slice(0, 18),
    [sortedEvents]
  );
  const bottomEvents = useMemo(
    () => (jointLeaveEvents.length > 0 ? jointLeaveEvents : sortedEvents.filter((e) => e.significance !== 'highest')).slice(0, 8),
    [jointLeaveEvents, sortedEvents]
  );

  const isIndonesiaCountry = lensLabel === 'Country' && entityLabel.toLowerCase() === 'indonesia';

  // Academic semester grouping
  const yearRange = useMemo(() => isAcademic ? getAcademicYearRange(sortedEvents) : '', [isAcademic, sortedEvents]);
  const semesters = useMemo(() => {
    if (!isAcademic) return null;
    const { odd, even } = groupBySemester(sortedEvents);
    return [
      { label: `Odd Semester (Jul – Dec ${yearRange.split('/')[0]})`, events: odd, semesterStart: findSemesterStartMs(odd) },
      { label: `Even Semester (Jan – Jun ${yearRange.split('/')[1]})`, events: even, semesterStart: findSemesterStartMs(even) },
    ].filter(s => s.events.length > 0);
  }, [isAcademic, sortedEvents, yearRange]);

  // Heading logic
  const heading = isAcademic
    ? 'Academic Calendar'
    : isIndonesiaCountry
      ? 'Hari Libur Nasional'
      : 'Holiday Intelligence';

  const subheading = isAcademic
    ? `${entityIcon} ${entityLabel} ${yearRange}`
    : isIndonesiaCountry
      ? `dan Cuti Bersama Tahun ${year}`
      : `${entityLabel} ${year}`;

  const description = isAcademic
    ? 'Key dates for the academic year at a glance.'
    : 'Poster-style summary for quick planning and sharing.';

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="rounded-3xl border border-border-primary bg-surface-card p-5 shadow-sm md:p-7">
        <div className="text-center">
          <p className="text-label font-medium uppercase tracking-widest text-muted">
            {entityIcon} {lensLabel} view
          </p>
          <h3 className="mt-2 font-heading text-3xl font-black leading-tight text-text-primary md:text-5xl">
            {heading}
          </h3>
          <p className="font-heading text-2xl font-semibold text-brand md:text-4xl">
            {subheading}
          </p>
          <p className="mt-2 text-xs text-text-tertiary md:text-sm">
            {description}
          </p>
        </div>

        {/* Main events grid — grouped by semester for academic, flat for others */}
        {isAcademic && semesters ? (
          semesters.map(({ label, events: semesterEvents, semesterStart }) => (
            <section key={label} className="mt-6">
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
                {label}
              </h4>
              <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                {semesterEvents.map((event, idx) => (
                  <EventCard key={event.id} event={event} idx={idx} showWeek semesterStart={semesterStart} lang={lang} />
                ))}
              </div>
            </section>
          ))
        ) : (
          <section className="mt-6">
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              {nationalEvents.map((event, idx) => (
                <EventCard key={event.id} event={event} idx={idx} lang={lang} />
              ))}
            </div>
          </section>
        )}

        {/* Bottom section — hidden for academic (no joint-leave, avoids duplicates) */}
        {!isAcademic && (
          <section className="mt-7">
            <div className="rounded-2xl border border-border-primary bg-surface-root p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
                {jointLeaveEvents.length > 0 ? `Cuti Bersama ${year}` : `Additional Dates ${year}`}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {bottomEvents.map((event, idx) => (
                  <article key={`bottom-${event.id}`} className="overflow-hidden content-card-elevated">
                    <div
                      className="px-2 py-1 text-center text-label font-semibold uppercase tracking-wider text-white"
                      style={{ backgroundColor: HEADER_COLORS[(idx + 2) % HEADER_COLORS.length] }}
                    >
                      {dateTag(event, lang)}
                    </div>
                    <div className="px-2.5 pb-2.5 pt-2 text-center">
                      <p className="text-label font-semibold leading-tight text-text-primary">
                        <a href={`/events/${getEventSlug(event.id)}`} className="hover:underline">{titleLine(event)}</a>
                      </p>
                      <p className="mt-1 text-label text-text-tertiary">{subLine(event)}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
